import { inject } from '@angular/core'
import { CanActivateFn, Router } from '@angular/router'
import { User, getAuth, signOut } from 'firebase/auth'
import { getFirebaseApp } from '../core/firebase'
import { AuthBridgeService } from '../core/services/auth-bridge.service'
import { OrgContextService } from '../core/services/org-context.service'
import { TeamsService } from '../core/services/teams.service'
import { environment } from '../../environments/environment'

/** Same cross-product onboarding target used by the production Svelte app. */
export const organizationSetupUrl = `${environment.adminConsoleUrl || 'https://admin.crewdle.ai'}/new`

export function redirectToOrganizationSetup(location: Pick<Location, 'href'> = window.location): void {
  location.href = organizationSetupUrl
}

// Refresh the cached `emailVerified` flag from Firebase (e.g. user clicked the
// verification link in another tab) before deciding whether to bounce them.
//
// Microsoft's OIDC sign-in doesn't surface an `email_verified` claim the way
// Google does, so Firebase leaves `emailVerified=false` after a successful
// signInWithPopup with microsoft.com — even though Microsoft has fully
// authenticated the user and asserted their email. Treat a linked
// microsoft.com provider as equivalent verification.
export async function isEmailVerified(user: User): Promise<boolean> {
  if (user.emailVerified) return true
  if (user.providerData.some((p) => p.providerId === 'microsoft.com')) return true
  try {
    await user.reload()
  } catch {
    // Don't lock the user out on a transient network failure.
    return true
  }
  return user.emailVerified
}

export const redirectUnauthorizedTo = (_redirect: string) => {
  return (() => {
    const guard: CanActivateFn = async (route, state) => {
      const router = inject(Router)
      const bridgeSvc = inject(AuthBridgeService)
      const orgContext = inject(OrgContextService)
      const teams = inject(TeamsService)

      const bridge = bridgeSvc.bridge!

      // Decide the Teams/non-Teams branch only AFTER detection resolves — never
      // read the signal synchronously here, or a Teams session gets misrouted
      // into the browser-only portal flow (which can't complete in the iframe).
      const inTeams = await teams.ensureTeamsDetected()

      // Resolve a local session, or exchange an authorization-code callback.
      // Works the same on localhost (against staging auth) and deployed origins.
      let user: User | null
      try {
        user = await bridge.ensureSession()
      } catch {
        user = null
      }

      // Inside Teams, run the token exchange once per app load — even when a
      // Firebase session already exists — so the workspace re-pins to the Teams
      // tenant's org (exchangeTeamsToken overwrites currentOrganizationId every
      // call), matching svelte. On failure with an existing session, load it
      // as-is rather than locking the user out (svelte auth store does the
      // same); with no session, fall through to the /teams-no-access branch.
      if (inTeams && teams.consumeAuthPending()) {
        try {
          await teams.performTeamsSso()
        } catch {
          // performTeamsSso already set ssoStatus/ssoError for the status page.
        }
        try {
          user = await bridge.ensureSession()
        } catch {
          // Keep the previously resolved session (if any).
        }
      }

      // No session. In Teams the portal login can't complete inside the iframe,
      // so send to the status page (retry / org setup) instead of bridge.login().
      if (!user) {
        if (inTeams) return router.parseUrl('/teams-no-access')
        await bridge.login()
        return false
      }

      // Skip email verification inside Teams — Entra already asserted the
      // identity, and the portal redirect can't complete in the iframe.
      if (!inTeams && !(await isEmailVerified(user))) {
        await signOut(getAuth(getFirebaseApp()!)).catch(() => {})
        await bridge.login()
        return false
      }

      // After a fresh code-exchange login the session is established here (late),
      // so OrgContext's bootstrap init() ran without a user. Load orgs now —
      // before the requireOrg child guard runs — so it doesn't briefly bounce
      // to /new while org data is still loading.
      if (!orgContext.currentOrg()) {
        try {
          await orgContext.init()
        } catch (error) {
          console.error('[AuthGuard] Organization bootstrap failed:', error)
          return false
        }
      }

      // Keep the Firebase/SSO session intact so it carries across to admin.
      // This is an external navigation, not an Angular route.
      if (!orgContext.currentOrg()) {
        redirectToOrganizationSetup()
        return false
      }

      // Strip the OAuth callback params from the URL. The bridge clears them via
      // history.replaceState, but Angular Router re-applies this navigation's
      // query params when it finalizes — so redirect to the clean path instead.
      if (route.queryParamMap.has('code')) {
        return router.parseUrl(state.url.split('?')[0])
      }

      return true
    }
    return guard
  })()
}

// Used by /login and /signup: if already signed in, go to the app; otherwise
// hand off to centralized auth instead of rendering an in-app login page.
export const redirectLoggedInTo = (redirect: string) => {
  return (() => {
    const guard: CanActivateFn = async () => {
      const router = inject(Router)
      const bridgeSvc = inject(AuthBridgeService)

      const bridge = bridgeSvc.bridge!

      let user: User | null
      try {
        user = await bridge.ensureSession()
      } catch {
        user = null
      }

      // Already signed in → into the app; otherwise hand off to the portal.
      if (user) return router.parseUrl(redirect)

      await bridge.login()
      return false
    }
    return guard
  })()
}

// Default export used directly as a route's `canActivate` guard
// (`redirectUnauthorizedTo` factory above, parameterized for the common case).
export const authGuard: CanActivateFn = redirectUnauthorizedTo('/login')
